import {
  Category as CategoryIcon,
  ExpandLess,
  ExpandMore,
  History as HistoryIcon,
  Inventory as InventoryIcon,
  People as PeopleIcon,
  PushPin as PushPinIcon,
  ShoppingBag as ShoppingBagIcon,
  ShoppingCart as ShoppingCartIcon,
  LibraryAdd as LibraryAddIcon,
  Campaign as CampaignIcon,
  Receipt as ReceiptIcon,
  Chat as ChatIcon,
  Dashboard as DashboardIcon,
} from '@mui/icons-material';
import MenuIcon from '@mui/icons-material/Menu';
import {
  Badge,
  Box,
  Collapse,
  Container,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import i18next from '../lang';
import { rootStore } from '../stores/RootStore';
import Footer from './Footer';
import { NaviMenuItem } from './NaviMenuItem';
import Header from './Header';
import axios from 'axios';
import WatercolorContainer from './WatercolorContainer';
import MessengerPopup from './MessengerPopup';
import { messageStore } from '../stores/MessageStore';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout = observer(({ children }: LayoutProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { authStore } = rootStore;
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [drawerPinned, setDrawerPinned] = React.useState(false);
  const drawerPinnedRef = React.useRef(false);
  const [menuOpenState, setMenuOpenState] = React.useState<{ [key: string]: boolean }>({});
  const [messengerOpen, setMessengerOpen] = React.useState(false);

  // メッセンジャー機能非表示のため、ポーリングを無効化
  // React.useEffect(() => {
  //   if (authStore.isAuthenticated) {
  //     messageStore.startPolling();
  //   }
  //   return () => messageStore.stopPolling();
  // }, [authStore.isAuthenticated]);

  const headerBadgeCount = messageStore.unreadCount;

  // ナビゲーション時にドロワーを閉じる（ピン固定時は維持）
  React.useEffect(() => {
    if (isMobile) {
      setDrawerOpen(false);
      setDrawerPinned(false);
      drawerPinnedRef.current = false;
    } else if (!drawerPinnedRef.current) {
      setDrawerOpen(false);
    }
  }, [location.pathname]);

  // モバイル⇔デスクトップ切り替え時のドロワー状態復元
  React.useEffect(() => {
    if (isMobile) {
      setDrawerOpen(false);
      setDrawerPinned(false);
      drawerPinnedRef.current = false;
    } else if (authStore.isAuthenticated) {
      // デスクトップに戻ったらピン固定で再オープン
      // temporary+open=false だと透明 Backdrop がクリックを吸収するバグの回避
      setDrawerOpen(true);
      setDrawerPinned(true);
      drawerPinnedRef.current = true;
    }
  }, [isMobile]);

  // ログイン後はサイドバーをピン固定状態でデフォルト表示
  React.useEffect(() => {
    if (authStore.isAuthenticated && !isMobile) {
      setDrawerOpen(true);
      setDrawerPinned(true);
      drawerPinnedRef.current = true;
    } else if (!authStore.isAuthenticated) {
      setDrawerOpen(false);
      setDrawerPinned(false);
      drawerPinnedRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authStore.isAuthenticated]);

  const toggleDrawer = () => {
    const newOpen = !drawerOpen;
    if (!newOpen) {
      setDrawerPinned(false);
      drawerPinnedRef.current = false;
    }

    setDrawerOpen(newOpen);
  };

  const toggleDrawerPin = () => {
    const newPinnedState = !drawerPinned;
    setDrawerPinned(newPinnedState);
    drawerPinnedRef.current = newPinnedState;
    if (newPinnedState) {
      setDrawerOpen(true);
    } else {
      setTimeout(() => {
        setDrawerOpen(false);
      }, 50);
    }
  };

  const toggleMenu = (menuId: string) => {
    setMenuOpenState(prev => ({
      ...prev,
      [menuId]: !prev[menuId]
    }));
  };

  let menuItems: NaviMenuItem[] = [
  ];

  if (authStore.isAuthenticated) {
    if (authStore.user?.level === 'ADMIN') {
      // 商品管理 → 商品購入設定 → 請求書確認 → 注文状況 → ユーザー管理 → お知らせ
      menuItems.push({
        id: 'product-management',
        text: i18next.t('nav.product_management'),
        icon: <ShoppingBagIcon />,
        subItems: [
          // { text: i18next.t('nav.product_category_management'), path: '/admin/product-categories', icon: <CategoryIcon /> },
          { text: i18next.t('nav.product_management'), path: '/admin/products', icon: <InventoryIcon /> },
          { text: i18next.t('nav.product_set_management'), path: '/admin/product-sets', icon: <ShoppingBagIcon /> }
        ]
      });
      menuItems.push({
        text: i18next.t('product_mapping.purchase_settings_title'),
        icon: <LibraryAddIcon />,
        path: '/admin/product-mappings'
      });
    } else {
      // 法人・ブランド・教場: 商品購入 → 注文状況 → 請求書確認 → ユーザー管理 → お知らせ
      menuItems.push({
        text: i18next.t('nav.purchase'),
        icon: <ShoppingCartIcon />,
        path: authStore.user?.level !== 'CLASSROOM_MANAGER' ? '/purchase/delivery-address' : '/purchase/delivery-datetime'
      });
    }
    if (authStore.user?.level === 'ADMIN') {
      // ADMIN: 請求書確認 → 注文状況 → ユーザー管理 → お知らせ
      menuItems.push({
        text: i18next.t('nav.invoice'),
        icon: <ReceiptIcon />,
        path: '/invoices'
      });
      menuItems.push({
        text: i18next.t('nav.order_status'),
        icon: <HistoryIcon />,
        path: '/orders'
      });
      menuItems.push({
        id: 'user-management',
        text: i18next.t('nav.user_management'),
        icon: <PeopleIcon />,
        path: '/admin/users'
      });
      menuItems.push({
        text: i18next.t('nav.announcements'),
        icon: <CampaignIcon />,
        path: '/admin/announcements'
      });
    } else {
      // 法人・ブランド・教場: 注文状況 → 請求書確認 → ユーザー管理 → お知らせ
      menuItems.push({
        text: i18next.t('nav.order_status'),
        icon: <HistoryIcon />,
        path: '/orders'
      });
      menuItems.push({
        text: i18next.t('nav.invoice'),
        icon: <ReceiptIcon />,
        path: '/invoices'
      });
      if (authStore.user?.level !== 'CLASSROOM_MANAGER') {
        menuItems.push({
          id: 'user-management',
          text: i18next.t('nav.user_management'),
          icon: <PeopleIcon />,
          path: '/admin/users'
        });
      }
      menuItems.push({
        text: i18next.t('nav.announcements'),
        icon: <CampaignIcon />,
        path: '/admin/announcements'
      });
    }
  }

  const getTemporaryOrder = async () => {
    try {
      const response = await axios.get('/api/v1/orders/temp');
      return response.data;

    } catch (error) {
      console.error('Failed to get temporary order：', error);
      return '';
    }
  }

  const handelClick = async (path) => {
    if (path === '/purchase/delivery-address' || path === '/purchase/delivery-datetime') {
      const tempOrder = await getTemporaryOrder();
      navigate(path, { state: { tempOrder } });
    } else {
      navigate(path);
    }
  }

  const renderMenuItems = (items: NaviMenuItem[], isAdmin = false) => (
    <List>
      {items.map((item) => (
        <React.Fragment key={item.id || item.path}>
          {item.subItems ? (
            <>
              <ListItem onClick={() => toggleMenu(item.id!)}>
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
                {menuOpenState[item.id!] ? <ExpandLess /> : <ExpandMore />}
              </ListItem>
              <Collapse in={menuOpenState[item.id!]} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {item.subItems.map((subItem) => (
                    <ListItem
                      key={subItem.path}
                      onClick={() => navigate(subItem.path)}
                      sx={{ pl: 4 }}
                    >
                      <ListItemIcon>{subItem.icon}</ListItemIcon>
                      <ListItemText primary={subItem.text} />
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            </>
          ) : (
            // <ListItem onClick={() => navigate(item.path!)}>
            <ListItem onClick={() => handelClick(item.path)}>
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItem>
          )}
        </React.Fragment>
      ))}
    </List>
  );

  const isDrawerEffectivelyOpen = authStore.isAuthenticated && drawerOpen && !isMobile;

  // パスからページタイトルを取得
  const getPageTitle = (pathname: string): string | undefined => {
    const titleMap: Record<string, string> = {
      '/admin/users': i18next.t('nav.user_management'),
      '/admin/products': i18next.t('nav.product_management'),
      '/admin/product-sets': i18next.t('nav.product_set_management'),
      '/admin/product-categories': i18next.t('nav.product_category_management'),
      '/admin/product-mappings': i18next.t('product_mapping.purchase_settings_title'),
      '/admin/announcements': i18next.t('nav.announcements'),
      '/orders': i18next.t('nav.order_status'),
      '/invoices': i18next.t('invoice.invoice_management'),
      '/profile': i18next.t('nav.profile'),
    };

    // 完全一致
    if (titleMap[pathname]) return titleMap[pathname];

    // パターンマッチ（動的ルート）
    if (/^\/admin\/users\/[\w]+/.test(pathname)) return i18next.t('user.detail_title');
    if (/^\/admin\/products\/create/.test(pathname)) return i18next.t('product.add');
    if (/^\/admin\/products\/edit\//.test(pathname)) return i18next.t('product.edit');
    if (/^\/admin\/product-sets\/create/.test(pathname)) return i18next.t('product_set.register');
    if (/^\/admin\/product-sets\/edit\//.test(pathname)) return i18next.t('product_set.edit');
    if (/^\/admin\/product-categories\/create/.test(pathname)) return i18next.t('nav.product_category_management');
    if (/^\/admin\/product-categories\/edit\//.test(pathname)) return i18next.t('nav.product_category_management');
    if (/^\/admin\/product-mappings\//.test(pathname)) return i18next.t('product_mapping.purchase_settings_title');
    if (/^\/admin\/announcements\//.test(pathname)) return i18next.t('nav.announcements');
    if (/^\/orders\//.test(pathname)) return i18next.t('nav.order_status');
    if (/^\/purchase\//.test(pathname)) return i18next.t('nav.purchase');

    return undefined;
  };

  const pageTitle = getPageTitle(location.pathname);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {authStore.isAuthenticated && <Drawer
        variant={isMobile ? 'temporary' : (drawerPinned ? "permanent" : "temporary")}
        open={drawerOpen}
        onClose={toggleDrawer}
        ModalProps={{ keepMounted: false }}
        BackdropProps={{ style: { backgroundColor: 'transparent' } }}
        PaperProps={{
          sx: {
            width: { xs: '80vw', sm: 280, md: 240 },
            maxWidth: 280,
            boxSizing: 'border-box',
            transition: 'all 0.3s ease-in-out',
            backgroundColor: 'rgba(255, 255, 255, 0.4)', // More transparent for watercolor visibility
            backdropFilter: 'blur(8px)',
            borderRight: '1px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '4px 0 24px rgba(0,0,0,0.05)',
            color: '#333',
          }
        }}
        sx={{
          zIndex: (theme) => theme.zIndex.appBar + 100, // Ensure it's on top
          '& .MuiBackdrop-root': {
            transition: 'opacity 0.3s ease-in-out',
          },
          '& .MuiListItem-root': {
            cursor: 'pointer',
          },
        }}
        transitionDuration={300}
        SlideProps={{
          easing: {
            enter: 'cubic-bezier(0.4, 0, 0.2, 1)',
            exit: 'cubic-bezier(0.4, 0, 0.2, 1)',
          },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: '64px' }}>
          <Box sx={{ ml: 3 }}>
            <IconButton edge="start" color="inherit" aria-label="menu" onClick={toggleDrawer} >
              <MenuIcon />
            </IconButton>
          </Box>

          {/* モバイルではピンボタンを非表示 */}
          {!isMobile && (
            <Box sx={{ mr: 1 }}>
              <IconButton onClick={toggleDrawerPin}>
                <PushPinIcon color={drawerPinned ? "primary" : "inherit"} />
              </IconButton>
            </Box>
          )}
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
          {renderMenuItems(menuItems, authStore.isAuthenticated)}
          <Box sx={{ flexGrow: 1 }} />
          {authStore.isAuthenticated && (
            <>
              {authStore.user?.level === 'ADMIN' && (
                <>
                  <Divider />
                  <ListItem
                    onClick={() => navigate('/admin/service-status')}
                    sx={{ cursor: 'pointer', backgroundColor: location.pathname.startsWith('/admin/service-status') ? 'action.selected' : 'transparent' }}
                  >
                    <ListItemIcon><DashboardIcon /></ListItemIcon>
                    <ListItemText primary={i18next.t('nav.service_status')} />
                  </ListItem>
                </>
              )}
              {/* メッセンジャー機能非表示 */}
              {/* <Divider />
              <ListItem onClick={() => setMessengerOpen(true)} sx={{ cursor: 'pointer' }}>
                <ListItemIcon>
                  <Badge badgeContent={messageStore.unreadCount} color="error" max={99}>
                    <ChatIcon />
                  </Badge>
                </ListItemIcon>
                <ListItemText primary={i18next.t('messenger.title')} />
              </ListItem> */}
              <Divider />
              <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                <PeopleIcon sx={{ fontSize: '1.2rem', color: '#666' }} />
                <Typography variant="caption" sx={{ color: '#666', fontSize: '0.75rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {authStore.user?.username}
                </Typography>
              </Box>
            </>
          )}
        </Box>
      </Drawer>}

      <Header
        toggleDrawer={toggleDrawer}
        drawerOpen={isDrawerEffectivelyOpen}
        navigate={navigate}
        authStore={authStore}
        adminMenuItems={[]}
        toggleMenu={toggleMenu}
        menuOpenState={menuOpenState}
        isMobile={isMobile}
        pageTitle={pageTitle}
        badgeCount={0}
        // onOpenMessenger={() => setMessengerOpen(true)} // メッセンジャー機能非表示
      />
      {i18next.language !== 'ja' && (
        <Box sx={{
          bgcolor: '#fff3e0',
          p: 0.5,
          textAlign: 'center',
          fontSize: '0.75rem',
          color: '#e65100',
          width: { xs: '100%', md: isDrawerEffectivelyOpen ? "calc(100% - 240px)" : "100%" },
          ml: { xs: 0, md: isDrawerEffectivelyOpen ? '240px' : 0 },
          transition: 'all 0.3s ease',
        }}>
          {i18next.t('common.auto_translation_notice')}
        </Box>
      )}
      <WatercolorContainer disableMargin={location.pathname === '/login'}>
        <Container component="main"
          maxWidth={false}
          disableGutters
          sx={{
            width: { xs: '100%', md: isDrawerEffectivelyOpen ? "calc(100% - 240px)" : "100%" },
            pr: 0, pl: 0, flex: 1, p: 0,
            ml: { xs: 0, md: isDrawerEffectivelyOpen ? '240px' : 0 },
            transition: 'all 0.3s ease',
            display: 'flex',
            flexDirection: 'column',
          }}>

          {children}
        </Container>
      </WatercolorContainer>
      <Footer drawerOpen={isMobile ? false : isDrawerEffectivelyOpen} isAuthenticated={authStore.isAuthenticated} />
      {/* メッセンジャー機能非表示 */}
      {/* <MessengerPopup open={messengerOpen} onClose={() => setMessengerOpen(false)} /> */}
    </Box>
  );
});

export default Layout;
